import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";
import { useSvgToolHandoffReceiver } from "../../hooks/useSvgToolHandoffReceiver";
import {
    extractViewBox,
    getSvgDimensions,
    isValidSvg,
} from "../../utils/svgUtils";
import { getAnchorCoordinates } from "./PathPopulateContext";
import {
    computePrimitivePointsPlacementsAsync,
    estimatePrimitivePointCount,
    roundDeg,
    type PrimitivePlacementMode,
    type PrimitiveObjectRotationMode,
    type PrimitivePointsProgress,
    type PrimitiveType,
} from "../../utils/primitivePointsPlacement";

interface UploadedSvg {
    name: string;
    content: string;
}

interface PrimitivePoint {
    x: number;
    y: number;
    depth: number;
    objectIndex: number;
    rotationDeg: number;
    faceId: string;
    flipX: boolean;
    flipY: boolean;
    isBackface: boolean;
}

interface PrimitivePointsState {
    primitiveType: PrimitiveType;
    subdivisions: number;
    rotationXDeg: number;
    rotationYDeg: number;
    rotationZDeg: number;
    cameraFovDeg: number;
    cameraDistance: number;
    viewBoxX: number;
    viewBoxY: number;
    viewBoxWidth: number;
    viewBoxHeight: number;
    backfaceCulling: boolean;
    objectPlacementMode: PrimitivePlacementMode;
    objectRotationMode: PrimitiveObjectRotationMode;
    objectSvgs: UploadedSvg[];
    scale: number;
    depthScaleEnabled: boolean;
    depthScaleStrength: number;
    baseRotationDeg: number;
    positionOffsetRandomness: number;
    flipXPercent: number;
    flipYPercent: number;
    flipOnBackfacesOnly: boolean;
    rotationRandomnessDeg: number;
    groupByFaceOnExport: boolean;
    polarRotationCenterXPct: number;
    polarRotationCenterYPct: number;
    anchor: string;
    error: string | null;
    placements: PrimitivePoint[];
    estimatedPlacementCount: number;
    placementComputePending: boolean;
    placementComputeProgress: PrimitivePointsProgress | null;
    objectInputRefs: React.MutableRefObject<(HTMLInputElement | null)[]>;
    setPrimitiveType: (value: PrimitiveType) => void;
    setSubdivisions: (value: number) => void;
    setRotationXDeg: (value: number) => void;
    setRotationYDeg: (value: number) => void;
    setRotationZDeg: (value: number) => void;
    setCameraFovDeg: (value: number) => void;
    setCameraDistance: (value: number) => void;
    setViewBoxX: (value: number) => void;
    setViewBoxY: (value: number) => void;
    setViewBoxWidth: (value: number) => void;
    setViewBoxHeight: (value: number) => void;
    setBackfaceCulling: (value: boolean) => void;
    setObjectPlacementMode: (value: PrimitivePlacementMode) => void;
    setObjectRotationMode: (value: PrimitiveObjectRotationMode) => void;
    setScale: (value: number) => void;
    setDepthScaleEnabled: (value: boolean) => void;
    setDepthScaleStrength: (value: number) => void;
    setBaseRotationDeg: (value: number) => void;
    setPositionOffsetRandomness: (value: number) => void;
    setFlipXPercent: (value: number) => void;
    setFlipYPercent: (value: number) => void;
    setFlipOnBackfacesOnly: (value: boolean) => void;
    setRotationRandomnessDeg: (value: number) => void;
    setGroupByFaceOnExport: (value: boolean) => void;
    setPolarRotationCenterXPct: (value: number) => void;
    setPolarRotationCenterYPct: (value: number) => void;
    setAnchor: (value: string) => void;
    setError: (value: string | null) => void;
    addObjectSvg: (svg: UploadedSvg) => void;
    removeObjectSvg: (index: number) => void;
    handleObjectFileLoad: (
        objectIndex?: number,
    ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
    generatePlacements: () => Promise<void>;
    cancelPlacementCompute: () => void;
    viewBoxAttr: string;
    objectInnerSvgs: Array<{
        content: string;
        viewBox: string;
        dimensions: { width: number; height: number } | null;
    }>;
    scaleFactor: number;
    canExport: boolean;
    buildExportSvgString: () => string;
    handleDownload: () => void;
    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
}

const PrimitivePointsContext = createContext<PrimitivePointsState | null>(null);

export function usePrimitivePoints() {
    const ctx = useContext(PrimitivePointsContext);
    if (!ctx) {
        throw new Error(
            "usePrimitivePoints must be used within PrimitivePointsProvider",
        );
    }
    return ctx;
}

function applyFillToSvgContent(svgContent: string, fillColor: string): string {
    if (!svgContent) return "";
    const elementRegex =
        /<(path|circle|rect|ellipse|polygon|polyline|line)([^>]*?)(\s*\/)?>/gi;
    return svgContent.replace(
        elementRegex,
        (_match, tagName, attributes, selfClose) => {
            const cleanedAttributes = String(attributes).replace(
                /\s*fill\s*=\s*["'][^"']*["']/gi,
                "",
            );
            const closing = selfClose ? " />" : ">";
            return `<${tagName}${cleanedAttributes} fill="${fillColor}"${closing}`;
        },
    );
}

export function PrimitivePointsProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    const [primitiveType, setPrimitiveType] = useState<PrimitiveType>("cube");
    const [subdivisions, setSubdivisions] = useState(2);
    const [rotationXDeg, setRotationXDeg] = useState(0);
    const [rotationYDeg, setRotationYDeg] = useState(0);
    const [rotationZDeg, setRotationZDeg] = useState(0);
    const [cameraFovDeg, setCameraFovDeg] = useState(55);
    const [cameraDistance, setCameraDistance] = useState(2.5);
    const [viewBoxX, setViewBoxX] = useState(0);
    const [viewBoxY, setViewBoxY] = useState(0);
    const [viewBoxWidth, setViewBoxWidth] = useState(200);
    const [viewBoxHeight, setViewBoxHeight] = useState(200);
    const [backfaceCulling, setBackfaceCulling] = useState(true);
    const [objectPlacementMode, setObjectPlacementMode] =
        useState<PrimitivePlacementMode>("alternating");
    const [objectRotationMode, setObjectRotationMode] =
        useState<PrimitiveObjectRotationMode>("fixed");
    const [objectSvgs, setObjectSvgs] = useState<UploadedSvg[]>([]);
    const [scale, setScale] = useState(12);
    const [depthScaleEnabled, setDepthScaleEnabled] = useState(false);
    const [depthScaleStrength, setDepthScaleStrength] = useState(0.5);
    const [baseRotationDeg, setBaseRotationDeg] = useState(0);
    const [positionOffsetRandomness, setPositionOffsetRandomness] = useState(0);
    const [flipXPercent, setFlipXPercent] = useState(0);
    const [flipYPercent, setFlipYPercent] = useState(0);
    const [flipOnBackfacesOnly, setFlipOnBackfacesOnly] = useState(false);
    const [rotationRandomnessDeg, setRotationRandomnessDeg] = useState(0);
    const [groupByFaceOnExport, setGroupByFaceOnExport] = useState(false);
    const [polarRotationCenterXPct, setPolarRotationCenterXPct] = useState(50);
    const [polarRotationCenterYPct, setPolarRotationCenterYPct] = useState(50);
    const [anchor, setAnchor] = useState("center");
    const [error, setError] = useState<string | null>(null);
    const [placements, setPlacements] = useState<PrimitivePoint[]>([]);
    const [placementComputePending, setPlacementComputePending] = useState(false);
    const [placementComputeProgress, setPlacementComputeProgress] =
        useState<PrimitivePointsProgress | null>(null);
    const objectInputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const placementAbortRef = useRef<AbortController | null>(null);
    const lastRotationRef = useRef({
        x: rotationXDeg,
        y: rotationYDeg,
        z: rotationZDeg,
    });

    const estimatedPlacementCount = useMemo(
        () => estimatePrimitivePointCount(primitiveType, subdivisions),
        [primitiveType, subdivisions],
    );

    const addObjectSvg = useCallback((svg: UploadedSvg) => {
        setObjectSvgs((prev) => [...prev, svg]);
    }, []);

    const removeObjectSvg = useCallback((index: number) => {
        setObjectSvgs((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const handleObjectFileLoad =
        (objectIndex?: number) =>
        (event: React.ChangeEvent<HTMLInputElement>): void => {
            const file = event.target.files?.[0];
            if (!file) return;
            setError(null);
            if (
                !file.type.includes("svg") &&
                !file.name.toLowerCase().endsWith(".svg")
            ) {
                setError("Please choose an SVG file.");
                return;
            }
            const reader = new FileReader();
            reader.onload = () => {
                const text = typeof reader.result === "string" ? reader.result : "";
                if (!text) {
                    setError("Unable to read SVG file.");
                    return;
                }
                if (!isValidSvg(text)) {
                    setError("Selected file does not appear to be valid SVG.");
                    return;
                }
                const payload: UploadedSvg = { name: file.name, content: text };
                if (objectIndex == null) {
                    addObjectSvg(payload);
                } else {
                    setObjectSvgs((prev) => {
                        const next = [...prev];
                        next[objectIndex] = payload;
                        return next;
                    });
                }
            };
            reader.onerror = () => setError("Error reading file.");
            reader.readAsText(file);
        };

    const objectInnerSvgs = useMemo(() => {
        return objectSvgs.map((svg) => {
            const innerMatch = svg.content.match(/<svg[^>]*>([\s\S]*?)<\/svg>/i);
            const innerContent = innerMatch ? innerMatch[1] : "";
            const viewBox = extractViewBox(svg.content)?.viewBox ?? "0 0 100 100";
            const dimensions = getSvgDimensions(svg.content);
            return {
                content: innerContent,
                viewBox,
                dimensions,
            };
        });
    }, [objectSvgs]);

    const scaleFactor = useMemo(() => {
        if (objectInnerSvgs.length === 0) return 1;
        const first = objectInnerSvgs[0];
        if (!first?.dimensions) return 1;
        const maxDimension = Math.max(first.dimensions.width, first.dimensions.height);
        if (maxDimension <= 0) return 1;
        return scale / maxDimension;
    }, [objectInnerSvgs, scale]);

    const viewBoxAttr = `${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}`;

    const generatePlacements = useCallback(async () => {
        if (objectSvgs.length === 0) {
            setError("Load at least one object SVG.");
            return;
        }
        setError(null);
        placementAbortRef.current?.abort();
        const ac = new AbortController();
        placementAbortRef.current = ac;
        setPlacementComputePending(true);
        setPlacementComputeProgress({
            phase: "generate",
            processed: 0,
            total: 1,
        });
        try {
            const points = await computePrimitivePointsPlacementsAsync({
                primitiveType,
                subdivisions,
                rotationXDeg,
                rotationYDeg,
                rotationZDeg,
                cameraFovDeg,
                cameraDistance,
                backfaceCulling,
                objectCount: objectSvgs.length,
                objectPlacementMode,
                objectRotationMode,
                polarRotationCenterXPct,
                polarRotationCenterYPct,
                positionOffsetRandomness,
                flipXPercent,
                flipYPercent,
                flipOnBackfacesOnly,
                rotationRandomnessDeg,
                outputMinX: viewBoxX,
                outputMinY: viewBoxY,
                outputWidth: viewBoxWidth,
                outputHeight: viewBoxHeight,
                signal: ac.signal,
                onProgress: (info) => setPlacementComputeProgress(info),
            });
            setPlacements(points);
        } catch (e) {
            if (!(e instanceof DOMException && e.name === "AbortError")) {
                console.warn("Primitive points compute failed:", e);
                setError("Could not generate primitive points.");
                setPlacements([]);
            }
        } finally {
            if (placementAbortRef.current === ac) {
                placementAbortRef.current = null;
            }
            setPlacementComputePending(false);
            setPlacementComputeProgress(null);
        }
    }, [
        objectSvgs.length,
        primitiveType,
        subdivisions,
        rotationXDeg,
        rotationYDeg,
        rotationZDeg,
        cameraFovDeg,
        cameraDistance,
        viewBoxX,
        viewBoxY,
        viewBoxWidth,
        viewBoxHeight,
        backfaceCulling,
        objectPlacementMode,
        objectRotationMode,
        polarRotationCenterXPct,
        polarRotationCenterYPct,
        positionOffsetRandomness,
        flipXPercent,
        flipYPercent,
        flipOnBackfacesOnly,
        rotationRandomnessDeg,
    ]);

    const cancelPlacementCompute = useCallback(() => {
        placementAbortRef.current?.abort();
        placementAbortRef.current = null;
        setPlacementComputePending(false);
        setPlacementComputeProgress(null);
    }, []);

    useEffect(() => {
        const prev = lastRotationRef.current;
        const didRotationChange =
            prev.x !== rotationXDeg ||
            prev.y !== rotationYDeg ||
            prev.z !== rotationZDeg;
        lastRotationRef.current = {
            x: rotationXDeg,
            y: rotationYDeg,
            z: rotationZDeg,
        };
        if (!didRotationChange) return;
        if (objectSvgs.length === 0) return;
        const timeoutId = setTimeout(() => {
            void generatePlacements();
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [
        rotationXDeg,
        rotationYDeg,
        rotationZDeg,
        objectSvgs.length,
        generatePlacements,
    ]);

    const canExport = objectInnerSvgs.length > 0 && placements.length > 0;

    const getDepthScaleMultiplier = useCallback(
        (depth: number, minDepth: number, maxDepth: number): number => {
            if (!depthScaleEnabled) return 1;
            const depthSpan = maxDepth - minDepth;
            if (!Number.isFinite(depthSpan) || Math.abs(depthSpan) < 1e-9) return 1;
            const farness = Math.max(
                0,
                Math.min(1, (depth - minDepth) / depthSpan),
            );
            const strength = Math.max(0, Math.min(1, depthScaleStrength));
            const minFactor = Math.max(0.1, 1 - strength * 0.8);
            return 1 - (1 - minFactor) * farness;
        },
        [depthScaleEnabled, depthScaleStrength],
    );

    const buildExportSvgString = useCallback(() => {
        if (!canExport) return "";
        let minDepth = Infinity;
        let maxDepth = -Infinity;
        for (const p of placements) {
            minDepth = Math.min(minDepth, p.depth);
            maxDepth = Math.max(maxDepth, p.depth);
        }
        const placementGroups = groupByFaceOnExport
            ? placements.reduce<Record<string, PrimitivePoint[]>>((acc, point) => {
                  const key = point.faceId || "face:unknown";
                  if (!acc[key]) acc[key] = [];
                  acc[key].push(point);
                  return acc;
              }, {})
            : { "face:all": placements };
        const objects = Object.entries(placementGroups)
            .map(([faceId, groupPoints]) => {
                const painterOrderedGroupPoints = [...groupPoints].sort(
                    (a, b) => b.depth - a.depth,
                );
                const groupObjects = painterOrderedGroupPoints
                    .map((point) => {
                        const objectSvg = objectInnerSvgs[point.objectIndex];
                        if (!objectSvg) return "";
                        let objectContent = objectSvg.content;
                        if (objectContent.includes("fill=\"currentColor\"")) {
                            objectContent = applyFillToSvgContent(
                                objectContent,
                                "#000000",
                            );
                        }
                        const dims = objectSvg.dimensions;
                        const anchorCoords = getAnchorCoordinates(anchor);
                        const offsetX = dims
                            ? -anchorCoords.x * dims.width * scaleFactor
                            : 0;
                        const offsetY = dims
                            ? -anchorCoords.y * dims.height * scaleFactor
                            : 0;
                        const depthScale = getDepthScaleMultiplier(
                            point.depth,
                            minDepth,
                            maxDepth,
                        );
                        const objectScale = scaleFactor * depthScale;
                        const centerX = dims ? dims.width * 0.5 : 0;
                        const centerY = dims ? dims.height * 0.5 : 0;
                        const transform = `translate(${point.x + offsetX}, ${point.y + offsetY}) rotate(${roundDeg(baseRotationDeg + point.rotationDeg)}, ${-offsetX}, ${-offsetY}) scale(${objectScale})`;
                        const flipTransform =
                            point.flipX || point.flipY
                                ? `translate(${centerX}, ${centerY}) scale(${point.flipX ? -1 : 1}, ${point.flipY ? -1 : 1}) translate(${-centerX}, ${-centerY})`
                                : "";
                        return flipTransform
                            ? `<g transform="${transform}"><g transform="${flipTransform}">${objectContent}</g></g>`
                            : `<g transform="${transform}">${objectContent}</g>`;
                    })
                    .join("\n");
                if (!groupByFaceOnExport) return groupObjects;
                return `<g data-face-id="${faceId}">\n${groupObjects}\n</g>`;
            })
            .join("\n");
        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBoxAttr}">
  <g class="objects">
${objects}
  </g>
</svg>`;
    }, [
        canExport,
        placements,
        objectInnerSvgs,
        anchor,
        scaleFactor,
        getDepthScaleMultiplier,
        baseRotationDeg,
        groupByFaceOnExport,
        viewBoxAttr,
    ]);

    const handleDownload = useCallback(() => {
        if (!canExport) return;
        const svgString = buildExportSvgString();
        if (!svgString) return;
        const blob = new Blob([svgString], {
            type: "image/svg+xml;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `primitive-points-${primitiveType}.svg`;
        a.click();
        URL.revokeObjectURL(url);
    }, [buildExportSvgString, canExport, primitiveType]);


    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({
            primitiveType,
            subdivisions,
            rotationXDeg,
            rotationYDeg,
            rotationZDeg,
            cameraFovDeg,
            cameraDistance,
            viewBoxX,
            viewBoxY,
            viewBoxWidth,
            viewBoxHeight,
            backfaceCulling,
            objectPlacementMode,
            scale,
            depthScaleEnabled,
            depthScaleStrength,
            baseRotationDeg,
            positionOffsetRandomness,
            flipXPercent,
            flipYPercent,
            flipOnBackfacesOnly,
            rotationRandomnessDeg,
            groupByFaceOnExport,
            objectRotationMode,
            polarRotationCenterXPct,
            polarRotationCenterYPct,
            anchor,
        }),
        [
            primitiveType,
            subdivisions,
            rotationXDeg,
            rotationYDeg,
            rotationZDeg,
            cameraFovDeg,
            cameraDistance,
            viewBoxX,
            viewBoxY,
            viewBoxWidth,
            viewBoxHeight,
            backfaceCulling,
            objectPlacementMode,
            objectRotationMode,
            scale,
            depthScaleEnabled,
            depthScaleStrength,
            baseRotationDeg,
            positionOffsetRandomness,
            flipXPercent,
            flipYPercent,
            flipOnBackfacesOnly,
            rotationRandomnessDeg,
            groupByFaceOnExport,
            anchor,
            polarRotationCenterXPct,
            polarRotationCenterYPct,
        ],
    );

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        if (
            values.primitiveType === "cube" ||
            values.primitiveType === "geodesic-sphere" ||
            values.primitiveType === "icosahedron" ||
            values.primitiveType === "torus" ||
            values.primitiveType === "capsule" ||
            values.primitiveType === "pyramid"
        ) {
            setPrimitiveType(values.primitiveType);
        }
        if (values.subdivisions != null) setSubdivisions(values.subdivisions as number);
        if (values.rotationXDeg != null) {
            setRotationXDeg(roundDeg(values.rotationXDeg as number));
        }
        if (values.rotationYDeg != null) {
            setRotationYDeg(roundDeg(values.rotationYDeg as number));
        }
        if (values.rotationZDeg != null) {
            setRotationZDeg(roundDeg(values.rotationZDeg as number));
        }
        if (values.cameraFovDeg != null) setCameraFovDeg(values.cameraFovDeg as number);
        if (values.cameraDistance != null) {
            setCameraDistance(values.cameraDistance as number);
        }
        if (values.viewBoxX != null) setViewBoxX(values.viewBoxX as number);
        if (values.viewBoxY != null) setViewBoxY(values.viewBoxY as number);
        if (values.viewBoxWidth != null) setViewBoxWidth(values.viewBoxWidth as number);
        if (values.viewBoxHeight != null) {
            setViewBoxHeight(values.viewBoxHeight as number);
        }
        if (values.backfaceCulling != null) {
            setBackfaceCulling(values.backfaceCulling as boolean);
        }
        if (
            values.objectPlacementMode === "alternating" ||
            values.objectPlacementMode === "random"
        ) {
            setObjectPlacementMode(values.objectPlacementMode);
        }
        if (
            values.objectRotationMode === "fixed" ||
            values.objectRotationMode === "path" ||
            values.objectRotationMode === "radial" ||
            values.objectRotationMode === "random" ||
            values.objectRotationMode === "polar-around-point" ||
            values.objectRotationMode === "polar-around-point-inverted"
        ) {
            setObjectRotationMode(values.objectRotationMode);
        }
        if (values.scale != null) setScale(values.scale as number);
        if (values.depthScaleEnabled != null) {
            setDepthScaleEnabled(values.depthScaleEnabled as boolean);
        }
        if (values.depthScaleStrength != null) {
            setDepthScaleStrength(values.depthScaleStrength as number);
        }
        if (values.baseRotationDeg != null) {
            setBaseRotationDeg(roundDeg(values.baseRotationDeg as number));
        }
        if (values.positionOffsetRandomness != null) {
            setPositionOffsetRandomness(values.positionOffsetRandomness as number);
        }
        if (values.flipXPercent != null) {
            setFlipXPercent(values.flipXPercent as number);
        }
        if (values.flipYPercent != null) {
            setFlipYPercent(values.flipYPercent as number);
        }
        if (values.flipOnBackfacesOnly != null) {
            setFlipOnBackfacesOnly(Boolean(values.flipOnBackfacesOnly));
        }
        if (values.rotationRandomnessDeg != null) {
            setRotationRandomnessDeg(roundDeg(values.rotationRandomnessDeg as number));
        }
        if (values.groupByFaceOnExport != null) {
            setGroupByFaceOnExport(Boolean(values.groupByFaceOnExport));
        }
        if (values.polarRotationCenterXPct != null) {
            setPolarRotationCenterXPct(values.polarRotationCenterXPct as number);
        }
        if (values.polarRotationCenterYPct != null) {
            setPolarRotationCenterYPct(values.polarRotationCenterYPct as number);
        }
        if (values.anchor != null) setAnchor(values.anchor as string);
    }, []);

    const { resetParameters } = useToolParamPersistence({
        toolId: "primitive-points",
        defaultPreset: {
            primitiveType: "cube",
            subdivisions: 2,
            rotationXDeg: 0,
            rotationYDeg: 0,
            rotationZDeg: 0,
            cameraFovDeg: 55,
            cameraDistance: 2.5,
            viewBoxX: 0,
            viewBoxY: 0,
            viewBoxWidth: 200,
            viewBoxHeight: 200,
            backfaceCulling: true,
            objectPlacementMode: "alternating",
            objectRotationMode: "fixed",
            scale: 12,
            depthScaleEnabled: false,
            depthScaleStrength: 0.5,
            baseRotationDeg: 0,
            positionOffsetRandomness: 0,
            flipXPercent: 0,
            flipYPercent: 0,
            flipOnBackfacesOnly: false,
            rotationRandomnessDeg: 0,
            groupByFaceOnExport: false,
            polarRotationCenterXPct: 50,
            polarRotationCenterYPct: 50,
            anchor: "center",
        },
        getPresetValues,
        applyPreset,
    });

    useSvgToolHandoffReceiver(
        "primitive-points",
        {
            onObject: (svg) => {
                setError(null);
                setObjectSvgs([svg]);
            },
            onPrimary: (svg) => {
                setError(null);
                setObjectSvgs([svg]);
            },
        },
        { setError },
    );

    const value: PrimitivePointsState = {
        primitiveType,
        subdivisions,
        rotationXDeg,
        rotationYDeg,
        rotationZDeg,
        cameraFovDeg,
        cameraDistance,
        viewBoxX,
        viewBoxY,
        viewBoxWidth,
        viewBoxHeight,
        backfaceCulling,
        objectPlacementMode,
        objectRotationMode,
        objectSvgs,
        scale,
        depthScaleEnabled,
        depthScaleStrength,
        baseRotationDeg,
        positionOffsetRandomness,
        flipXPercent,
        flipYPercent,
        flipOnBackfacesOnly,
        rotationRandomnessDeg,
        groupByFaceOnExport,
        polarRotationCenterXPct,
        polarRotationCenterYPct,
        anchor,
        error,
        placements,
        estimatedPlacementCount,
        placementComputePending,
        placementComputeProgress,
        objectInputRefs,
        setPrimitiveType,
        setSubdivisions,
        setRotationXDeg,
        setRotationYDeg,
        setRotationZDeg,
        setCameraFovDeg,
        setCameraDistance,
        setViewBoxX,
        setViewBoxY,
        setViewBoxWidth,
        setViewBoxHeight,
        setBackfaceCulling,
        setObjectPlacementMode,
        setObjectRotationMode,
        setScale,
        setDepthScaleEnabled,
        setDepthScaleStrength,
        setBaseRotationDeg,
        setPositionOffsetRandomness,
        setFlipXPercent,
        setFlipYPercent,
        setFlipOnBackfacesOnly,
        setRotationRandomnessDeg,
        setGroupByFaceOnExport,
        setPolarRotationCenterXPct,
        setPolarRotationCenterYPct,
        setAnchor,
        setError,
        addObjectSvg,
        removeObjectSvg,
        handleObjectFileLoad,
        generatePlacements,
        cancelPlacementCompute,
        viewBoxAttr,
        objectInnerSvgs,
        scaleFactor,
        canExport,
        buildExportSvgString,
        handleDownload,
        applyPreset,
        getPresetValues,
        resetParameters,
    };

    return (
        <PrimitivePointsContext.Provider value={value}>
            {children}
        </PrimitivePointsContext.Provider>
    );
}
